import React, { useState } from 'react'
import { Container, Accordion, Button, Spinner } from 'react-bootstrap'
import {
	DeleteOptionReq,
	PostNewOptionReq,
	PostOptionReq,
	RuleReq,
	translatedKeys,
} from '../types'
import EditTable from '../components/EditTable'
import AddRuleModal from '../components/AddRuleModal'
import AddOptionModal from '../components/AddOptionModal'
import {
	useGetOptionsQuery,
	useGetRulesQuery,
	useChangeOptionMutation,
	useDeleteOptionMutation,
	useAddOptionMutation,
	usePostRuleMutation,
	useDeleteRuleMutation,
} from '../store/api'
import useHasPermission from '../hooks/useHasPermission'

const EditCategoriesPage: React.FC = () => {
	const [isOpenAddRuleModal, setIsOpenAddRuleModal] = useState(false)
	const [isOpenAddOptionModal, setIsOpenAddOptionModal] = useState(false)
	const hasRulesIndexPermission = useHasPermission('rules.index')
	const hasOptionsStorePermission = useHasPermission('options.items.store')
	const hasRulesStorePermission = useHasPermission('rules.store')

	const { data: options, isLoading: isLoadingOptions } = useGetOptionsQuery()
	const { data: rules } = useGetRulesQuery()

	const [changeOption, { isLoading: isLoadingPostOption }] =
		useChangeOptionMutation()
	const [deleteOption, { isLoading: isLoadingDeleteOption }] =
		useDeleteOptionMutation()
	const [addOption, { isLoading: isLoadingPostNewOption }] =
		useAddOptionMutation()
	const [postRule, { isLoading: isLoadingPostRule }] = usePostRuleMutation()
	const [deleteRule, { isLoading: isLoadingDeleteRule }] =
		useDeleteRuleMutation()

	const handlePostOption = async (req: PostOptionReq) => {
		try {
			await changeOption(req).unwrap()
		} catch (error) {
			console.error('Failed to change option:', error)
		}
	}

	const handleDeleteOption = async (req: DeleteOptionReq) => {
		try {
			await deleteOption(req).unwrap()
		} catch (error) {
			console.error('Failed to delete option:', error)
		}
	}

	const handlePostNewOption = async (req: PostNewOptionReq) => {
		try {
			await addOption(req).unwrap()
			setIsOpenAddOptionModal(false)
		} catch (error) {
			console.error('Failed to add option:', error)
		}
	}

	const handlePostRule = async (req: RuleReq) => {
		try {
			await postRule(req).unwrap()
			setIsOpenAddRuleModal(false)
		} catch (error) {
			console.error('Failed to post rule:', error)
		}
	}

	const handleDeleteRule = async (req: RuleReq) => {
		try {
			await deleteRule(req).unwrap()
		} catch (error) {
			console.error('Failed to delete rule:', error)
		}
	}

	if (!hasRulesIndexPermission) {
		return (
			<Container className="py-4">
				<h2 className="mb-4">Категорії</h2>
				<p>У вас немає прав доступу до цієї сторінки.</p>
			</Container>
		)
	}

	if (isLoadingOptions || !options || !rules) {
		return (
			<Container className="d-flex justify-content-center align-items-center" style={{ minHeight: '400px' }}>
				<Spinner animation="border" role="status">
					<span className="visually-hidden">Завантаження...</span>
				</Spinner>
			</Container>
		)
	}

	return (
		<Container className="py-4">
			<h2 className="mb-4">Категорії</h2>
			<div className="mb-3">
				{hasOptionsStorePermission && (
					<Button variant="primary" className="me-2" onClick={() => setIsOpenAddOptionModal(true)}>
						Додати категорію
					</Button>
				)}
				{hasRulesStorePermission && (
					<Button variant="primary" onClick={() => setIsOpenAddRuleModal(true)}>
						Додати правило
					</Button>
				)}
			</div>
			<Accordion>
				{Object.keys(translatedKeys).map((key) => {
					const translatedKey = key as keyof typeof translatedKeys
					return (
						<Accordion.Item eventKey={translatedKey} key={translatedKey}>
							<Accordion.Header>
								{translatedKeys[translatedKey]}
							</Accordion.Header>
							<Accordion.Body>
								<EditTable
									isSuccessDeleteRule={false}
									isSuccessPostOption={false}
									isLoadingDeleteRule={isLoadingDeleteRule}
									isLoadingPostOption={isLoadingPostOption}
									fetchDeleteRule={handleDeleteRule}
									fetchPostOption={handlePostOption}
									type={translatedKey}
									rules={rules}
									options={options}
									fetchDeleteOption={handleDeleteOption}
									isLoadingDeleteOption={isLoadingDeleteOption}
									isSuccessDeleteOption={false}
								/>
							</Accordion.Body>
						</Accordion.Item>
					)
				})}
			</Accordion>
			<AddRuleModal
				isOpen={isOpenAddRuleModal}
				onClose={() => setIsOpenAddRuleModal(false)}
				options={options}
				save={handlePostRule}
				isLoading={isLoadingPostRule}
			/>
			<AddOptionModal
				isOpen={isOpenAddOptionModal}
				onClose={() => setIsOpenAddOptionModal(false)}
				save={handlePostNewOption}
				isLoading={isLoadingPostNewOption}
			/>
		</Container>
	)
}

export default EditCategoriesPage

